import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Menu from 'semantic-ui-react/dist/commonjs/collections/Menu';
import Sidebar from 'semantic-ui-react/dist/commonjs/modules/Sidebar';
import Transition from 'semantic-ui-react/dist/commonjs/modules/Transition';
import AppRoutes from '../../components/app-routes/AppRoutes';
import CommonMessage from '../../components/common-message/CommonMessage';
import Footer from '../../components/footer/Footer';
import HeaderNavbar from '../../components/header/HeaderNavbar';
import MobileSidebar from '../sidebar/Sidebar';
import {
  DEVICE_DESKTOP,
  DEVICE_MOBILE,
  SIGNIN_URL,
  SIGNUP_URL,
  REMOVE_TRANSFORM_FROM_SCREENS,
  PROD_ENVIRONMENT,
  IPADS_ALTERNATE_WIDTH,
  SIGNIN_META_TITLE,
  SIGNIN_META_DESC,
  SIGNUP_META_TITLE,
  SIGNUP_META_DESC,
  OTHERS_META_TITLE,
  OTHERS_META_DESC,
  FAQ_LINK_URL,
  GOOGLE_RECAPTCHA_PUBLIC_URL
} from '../../utils/constants';
import { injectIntoDataLayer } from '../../utils/library';
import { ModalFactory } from '../../components/modal/modal';
import ToggleFeatureProvider, { ToggleFeatureConsumer } from '../../toggleFeature';
import { switchDevice, toggleSidebar } from '../../redux/actions/ui-trigger/ui-trigger-action';
import { ThemeProviderContainer } from '../../screens/user-flow/sendmoney/themeContext';
import { BUILD } from '../../utils/config';
import { fetchFlagsFromFlagsmith } from '../../redux/actions/flagsmith/flagsmith';
import { updateStatsigUser } from '../../utils/statsig';
import { LOADER_ICON } from '@Images';
import { optimizeGTMUrl } from '../../constants/evs/optimizeGtmUrl';
import {
  getUserFlowReducer,
  selectUiTriggerStateReducer,
  selectUserDataReducer,
  selectUserSession
} from '@Redux/selectors';
import { hideMessage } from '@Redux/actions/common-message/message-action';

const App = () => {
  const location = useLocation();
  const nodeRef = useRef(null);
  const headerRef = useRef(null);
  const toggleMenuRef = useRef(null);
  const dispatch = useDispatch();
  const user_session = useSelector(selectUserSession);
  const { currentScreen } = useSelector(getUserFlowReducer);
  const { toggleFeature } = useSelector(selectUserDataReducer);
  const { loaderState, scroll, sidebarVisibility, device, loaderConfig } = useSelector(
    selectUiTriggerStateReducer
  );
  const { customer_id: customerId, account_type, additional_info } = user_session;

  const saveHelpCenterUrl = () => {
    const currentUrl = window.location.href;
    sessionStorage.setItem(FAQ_LINK_URL, currentUrl);
  };

  const setMetaTitleAndDescriptionWithTitle = (title, description) => {
    const metaTitleTag = document.querySelector("meta[name='title']");
    const metaDescTag = document.querySelector("meta[name='description']");
    metaTitleTag?.setAttribute('content', title);
    metaDescTag?.setAttribute('content', description);
    document.title = title;
  };

  const onKeyUp = ({ keyCode }) => {
    if (keyCode !== 27) return;
    dispatch(hideMessage());
  };

  const scrollToTop = () => {
    if (nodeRef.current) {
      nodeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
    }
  };

  const onLocationChanged = ({ pathname }) => {
    injectIntoDataLayer('url-change', { pathname });
    if (nodeRef.current) {
      nodeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'start'
      });
    }
  };

  const blurPopup = () => {
    const toggleMenu = toggleMenuRef.current;
    toggleMenu && toggleMenu();
  };

  // Keeping useCallback only for functions that depend on props and are used in useEffect
  const onResize = useCallback(
    ({ target: { innerWidth } }) => {
      const newDevice = innerWidth <= IPADS_ALTERNATE_WIDTH ? DEVICE_MOBILE : DEVICE_DESKTOP;
      if (newDevice !== device) {
        dispatch(switchDevice(newDevice));
      }
    },
    [device, dispatch]
  );

  useEffect(() => {
    const fetchFlagsmithFlags = async () => {
      await dispatch(fetchFlagsFromFlagsmith());
    };
    fetchFlagsmithFlags();
  }, [dispatch]);

  useEffect(() => {
    const { search } = location;
    const params1 = new URLSearchParams(search);
    const faqArray = params1.get('faqLink');

    if (faqArray) {
      saveHelpCenterUrl();
    }

    if ([PROD_ENVIRONMENT].includes(BUILD) && optimizeGTMUrl) {
      const script = document.createElement('script');
      script.src = optimizeGTMUrl;
      document.head.appendChild(script);
    }

    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('resize', onResize);

    // Listen for location changes using useEffect dependency
    onLocationChanged(location);

    return () => {
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, onResize]);

  useEffect(() => {
    if (scroll) {
      scrollToTop();
    }
  }, [scroll]);

  useEffect(() => {
    const pathname = location?.pathname;

    if ([SIGNIN_URL].includes(pathname)) {
      setMetaTitleAndDescriptionWithTitle(SIGNIN_META_TITLE, SIGNIN_META_DESC);
    } else if ([SIGNUP_URL].includes(pathname)) {
      setMetaTitleAndDescriptionWithTitle(SIGNUP_META_TITLE, SIGNUP_META_DESC);
    } else {
      setMetaTitleAndDescriptionWithTitle(OTHERS_META_TITLE, OTHERS_META_DESC);
    }

    // Hide static loader as soon as App mounts
    const staticLoader = document.getElementById('static-loader');
    const appContainer = document.getElementById('app');

    if (staticLoader) {
      staticLoader.classList.add('fade-out');
      staticLoader.parentNode.removeChild(staticLoader);
    }

    if (appContainer) {
      appContainer.classList.add('loaded');
    }
  }, [location?.pathname]);

  useEffect(() => {
    if (customerId) {
      updateStatsigUser();
    }
  }, [customerId]);

  const removeTransformTrigger =
    toggleFeature &&
    toggleFeature.toggleFeature &&
    REMOVE_TRANSFORM_FROM_SCREENS.includes(currentScreen);

  const removeTransform = removeTransformTrigger ? { transform: 'none' } : {};

  return (
    <div className="app" ref={nodeRef}>
      <CommonMessage />
      {loaderState && (
        <Transition visible={loaderState} duration={300}>
          <div
            className={`app-loader-wrapper ${
              loaderConfig && loaderConfig.isBGTransparent === false ? 'non-transparent-bg' : ''
            }`}
            id="loading"
          >
            <img src={LOADER_ICON} alt="loader" className="app-loader" />
          </div>
        </Transition>
      )}
      <Sidebar.Pushable style={removeTransform}>
        {device === DEVICE_MOBILE && (
          <ToggleFeatureProvider>
            <ToggleFeatureConsumer>
              <Sidebar
                animation="overlay"
                visible={sidebarVisibility}
                direction="right"
                vertical
                inline={'inline'}
                style={{ minWidth: '89%' }}
                as={Menu}
              >
                <MobileSidebar />
              </Sidebar>
            </ToggleFeatureConsumer>
          </ToggleFeatureProvider>
        )}
        <Sidebar.Pusher
          dimmed={sidebarVisibility}
          onClick={() => {
            if (sidebarVisibility) dispatch(toggleSidebar());
            blurPopup();
          }}
        >
          <ThemeProviderContainer>
            <ToggleFeatureProvider>
              <ToggleFeatureConsumer>
                {window.location.pathname !== GOOGLE_RECAPTCHA_PUBLIC_URL ? (
                  <>
                    <HeaderNavbar
                      provideToggleMenuParams={(navbar) =>
                        (toggleMenuRef.current = () => {
                          let {
                            state: { openMenu, notification },
                            toggleMenu,
                            closeNotification
                          } = navbar;
                          openMenu && toggleMenu();
                          notification && closeNotification && closeNotification();
                        })
                      }
                      hard_sme_migrated={
                        account_type === 'CORPORATE' &&
                        additional_info &&
                        additional_info.hard_sme_migrated
                      }
                      headerRef={headerRef}
                      device={device}
                    />
                  </>
                ) : (
                  <></>
                )}
                <AppRoutes
                  countryCode={user_session.country_code}
                  hard_sme_migrated={
                    account_type === 'CORPORATE' &&
                    additional_info &&
                    additional_info.hard_sme_migrated
                  }
                  headerRef={headerRef}
                />

                {window.location.pathname !== GOOGLE_RECAPTCHA_PUBLIC_URL ? (
                  <Footer user_session={user_session} />
                ) : (
                  <></>
                )}
              </ToggleFeatureConsumer>
            </ToggleFeatureProvider>
          </ThemeProviderContainer>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
      {ModalFactory.instance}
    </div>
  );
};

export default App;
